import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@sphinx/design-system/button";
import { Switch } from "@sphinx/design-system/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sphinx/design-system/popover";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { useHostList, useHostMutations } from "@/hooks/useClients";
import { usePreviewedHostId } from "@/hooks/use-previewed-client-id";
import { effectiveModels, modelsForClient as modelsForTarget } from "@/lib/client-models";
import { clientKeepsConfigTab } from "@/lib/client-config-tab";
import { resolveHostLogoByName } from "@/lib/host-logo";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import type { SphinxModel } from "@/shared/sphinx";

/**
 * The Client Connectors header: a CLIENT NAVIGATOR, not a tab bar.
 *
 * sphinx does not emulate clients — it drives the real ones — so "which
 * client" is not a mode the page is in, it is the thing the page is about.
 * The logo is big and centred with a pager either side so it reads as
 * something you move between; everything below (the connector cards) is
 * scoped to whatever is selected here.
 *
 * The selection IS the project's previewed client (`usePreviewedHostId`), the
 * same value the Playground and the client canvas read, so switching here
 * follows you around instead of being a fourth private notion of "current
 * client". Switching it is a UI change only: no connection is torn down (see
 * `useAutoConnectProjectServers`).
 *
 * Three controls, all persisted straight to `PATCH /api/sphinx/targets/:id`:
 * the enable toggle (`enabled`), the model picker (`models`, several allowed,
 * family-pinned) and — on the cards below — the connector attachments
 * (`serverIds`).
 */

interface ClientConnectorsHeaderProps {
  projectId: string | null;
  isAuthenticated: boolean;
  /** Which of the header's own destinations is open. */
  view: "servers" | "host" | "skills";
  onOpenConnectors: () => void;
  onOpenClientConfig: (hostId: string) => void;
  onOpenSkills: () => void;
  /** Right-hand slot (the Connectors view's "add server" portal target). */
  rightSlot?: ReactNode;
  showSkills?: boolean;
  testId?: string;
}

export function ClientConnectorsHeader({
  projectId,
  isAuthenticated,
  view,
  onOpenConnectors,
  onOpenClientConfig,
  onOpenSkills,
  rightSlot,
  showSkills = true,
  testId = "client-connectors-header",
}: ClientConnectorsHeaderProps) {
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const { hosts, isLoading } = useHostList({ isAuthenticated, projectId });
  const [selectedHostId, setSelectedHostId] = usePreviewedHostId(projectId);
  const { setTargetEnabled, setTargetModels } = useHostMutations();
  const [busy, setBusy] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);

  const index = hosts.findIndex((h) => h.hostId === selectedHostId);
  const selected = index >= 0 ? hosts[index] : null;

  // A page about one client needs one selected. Land on the first ENABLED
  // client (the ones a run would use) rather than whatever sorts first, and
  // repair a stored id that is no longer in the matrix.
  useEffect(() => {
    if (isLoading || hosts.length === 0 || selected) return;
    const fallback = hosts.find((h) => h.enabled) ?? hosts[0];
    setSelectedHostId(fallback.hostId);
  }, [isLoading, hosts, selected, setSelectedHostId]);

  const step = useCallback(
    (delta: number) => {
      if (hosts.length === 0) return;
      const from = index >= 0 ? index : 0;
      const next = (from + delta + hosts.length) % hosts.length;
      setSelectedHostId(hosts[next].hostId);
    },
    [hosts, index, setSelectedHostId]
  );

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update client");
    } finally {
      setBusy(false);
    }
  }, []);

  const allowedModels = useMemo(
    () => (selected ? modelsForTarget(selected.hostId) : []),
    [selected]
  );
  const chosenModels = useMemo(
    () => (selected ? effectiveModels(selected) : []),
    [selected]
  );

  const toggleModel = (model: SphinxModel) => {
    if (!selected) return;
    const has = chosenModels.includes(model);
    const next = has
      ? chosenModels.filter((m) => m !== model)
      : [...chosenModels, model];
    if (next.length === 0) {
      toast.error("A client needs at least one model");
      return;
    }
    void run(() => setTargetModels({ hostId: selected.hostId, models: next }));
  };

  const destinations = [
    { value: "servers" as const, label: "Connectors" },
    // The Client tab is the emulation/config canvas. sphinx drives real
    // clients, so there is nothing to configure for eight of them — the two
    // direct-API clients are the exception, where configuration is the whole
    // client.
    ...(clientKeepsConfigTab(selected?.hostId ?? null)
      ? ([{ value: "host" as const, label: "Client" }] as const)
      : []),
    ...(showSkills ? ([{ value: "skills" as const, label: "Skills" }] as const) : []),
  ];

  return (
    <div
      className="@container relative shrink-0 border-b border-border/40 px-4 py-3 md:px-8"
      data-testid={testId}
    >
      <div className="flex flex-col items-stretch gap-3 @2xl:grid @2xl:grid-cols-[1fr_auto_1fr] @2xl:items-center @2xl:gap-3">
        <div className="hidden @2xl:block" aria-hidden="true" />

        <div className="flex min-w-0 flex-col items-center gap-2">
          {isLoading && !selected ? (
            <div
              className="h-16 w-48 animate-pulse rounded-xl bg-muted/50"
              data-testid="client-connectors-header-loading"
            />
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Previous client"
                  data-testid="client-nav-prev"
                  disabled={hosts.length < 2}
                  onClick={() => step(-1)}
                  className="size-8 rounded-full text-muted-foreground"
                >
                  <ChevronLeft className="size-4" />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Select client"
                      data-testid="client-nav-current"
                      className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl px-4 py-1.5 outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/45 data-[state=open]:bg-muted/50"
                    >
                      <img
                        src={resolveHostLogoByName(
                          selected?.name ?? "",
                          themeMode
                        )}
                        alt=""
                        className="size-10 shrink-0 object-contain"
                      />
                      <span className="flex items-center gap-1.5">
                        <span className="max-w-[16rem] truncate text-base font-semibold">
                          {selected?.name ?? "No client"}
                        </span>
                        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="bottom"
                    align="center"
                    sideOffset={8}
                    className="min-w-[15rem]"
                  >
                    <DropdownMenuRadioGroup
                      value={selected?.hostId ?? ""}
                      onValueChange={(next) => setSelectedHostId(next)}
                    >
                      {hosts.map((host) => (
                        <DropdownMenuRadioItem
                          key={host.hostId}
                          value={host.hostId}
                          hideIndicator
                          className="gap-2.5 py-2"
                          data-testid={`client-option-${host.hostId}`}
                        >
                          <img
                            src={resolveHostLogoByName(host.name, themeMode)}
                            alt=""
                            className="size-4 shrink-0 object-contain"
                          />
                          <span className="flex-1 truncate">{host.name}</span>
                          {host.enabled ? (
                            <span
                              aria-hidden
                              className="size-1.5 shrink-0 rounded-full bg-primary"
                            />
                          ) : null}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Next client"
                  data-testid="client-nav-next"
                  disabled={hosts.length < 2}
                  onClick={() => step(1)}
                  className="size-8 rounded-full text-muted-foreground"
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>

              {selected ? (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <span className="rounded-full border border-border/60 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                    {index + 1} / {hosts.length}
                  </span>

                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground select-none">
                    <Switch
                      checked={!!selected.enabled}
                      disabled={busy}
                      aria-label={`Enable ${selected.name}`}
                      data-testid="client-enabled-toggle"
                      onCheckedChange={(enabled) =>
                        void run(() =>
                          setTargetEnabled({
                            hostId: selected.hostId,
                            enabled,
                          })
                        )
                      }
                    />
                    <span>{selected.enabled ? "Enabled" : "Disabled"}</span>
                  </label>

                  <Popover open={modelsOpen} onOpenChange={setModelsOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        className="h-7 gap-1.5 text-xs"
                        data-testid="client-models"
                        title="Every model this client runs. A run expands to case × client × model."
                      >
                        <span className="max-w-[16rem] truncate">
                          {chosenModels.length === 0
                            ? "Provider default"
                            : chosenModels.join(", ")}
                        </span>
                        <ChevronDown className="size-3 shrink-0 opacity-60" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="center" className="w-64 p-1">
                      <p className="px-2 py-1.5 text-[11px] text-muted-foreground">
                        Every run expands to case × client × model.
                      </p>
                      {allowedModels.map((model) => {
                        const checked = chosenModels.includes(model);
                        return (
                          <button
                            key={model}
                            type="button"
                            data-testid={`client-model-${model}`}
                            aria-pressed={checked}
                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                            onClick={() => toggleModel(model)}
                          >
                            <span
                              className={cn(
                                "flex size-4 items-center justify-center rounded-[4px] border",
                                checked
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border"
                              )}
                            >
                              {checked ? (
                                <Check className="size-3" strokeWidth={3} />
                              ) : null}
                            </span>
                            <span className="truncate">{model}</span>
                          </button>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                </div>
              ) : null}

              {destinations.length > 1 ? (
                <nav
                  aria-label="Client Connectors view"
                  className="flex items-center gap-1"
                >
                  {destinations.map((destination) => (
                    <button
                      key={destination.value}
                      type="button"
                      aria-current={
                        destination.value === view ? "page" : undefined
                      }
                      data-testid={`client-connectors-view-${destination.value}`}
                      onClick={() => {
                        if (destination.value === "servers") onOpenConnectors();
                        else if (destination.value === "skills") onOpenSkills();
                        else if (selected) onOpenClientConfig(selected.hostId);
                      }}
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                        destination.value === view
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {destination.label}
                    </button>
                  ))}
                </nav>
              ) : null}
            </>
          )}
        </div>

        {rightSlot ?? <div className="hidden @2xl:block" aria-hidden="true" />}
      </div>
    </div>
  );
}
